import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths } from "date-fns";

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Icons
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Play,
  Mic,
  Video,
  FileText,
  CheckCircle2,
  Clock,
  Loader2
} from "lucide-react";

const ContentCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("month"); // month, list

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await api.get(`/videos/calendar/view?year=${year}&month=${month}`);
      setCalendarData(response.data.scheduled_videos || {});
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-3 h-3 text-green-500" />;
      case 'generating':
        return <Loader2 className="w-3 h-3 text-primary animate-spin" />;
      case 'approved':
        return <CheckCircle2 className="w-3 h-3 text-accent" />;
      default:
        return <Clock className="w-3 h-3 text-muted-foreground" />;
    }
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const days = getDaysInMonth();
  const startDay = startOfMonth(currentDate).getDay();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="calendar-loading">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-[600px] rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-calendar">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">
            Content Calendar
          </h1>
          <p className="text-muted-foreground mt-1">
            365 days of Abraham's journey, scheduled and ready
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-[120px]" data-testid="view-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="list">List</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card className="card-patriarch">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigateMonth('prev')}
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-xl font-display font-semibold">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigateMonth('next')}
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'month' ? (
            <>
              {/* Week Days Header */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day) => (
                  <div 
                    key={day} 
                    className="text-center text-xs font-medium text-muted-foreground py-2"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                {/* Empty cells for days before the first of the month */}
                {[...Array(startDay)].map((_, index) => (
                  <div key={`empty-${index}`} className="calendar-day opacity-0" />
                ))}

                {/* Days of the month */}
                {days.map((day) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const content = calendarData[dateStr];
                  const isCurrentDay = isToday(day);

                  return (
                    <TooltipProvider key={dateStr}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={`calendar-day ${content ? 'has-content' : ''} ${isCurrentDay ? 'today' : ''}`}
                            data-testid={`calendar-day-${format(day, 'd')}`}
                          >
                            <div className="flex flex-col h-full">
                              <span className={`text-sm font-medium ${isCurrentDay ? 'text-primary' : ''}`}>
                                {format(day, 'd')}
                              </span>
                              {content && (
                                <div className="flex-1 flex flex-col justify-end">
                                  <div className="flex items-center gap-1 mt-1">
                                    {getStatusIcon(content.status)}
                                    <span className="text-xs truncate hidden sm:block">
                                      Day {content.day_number}
                                    </span>
                                  </div>
                                  <div className="flex gap-1 mt-1">
                                    {content.has_voice && (
                                      <Mic className="w-3 h-3 text-green-500" />
                                    )}
                                    {content.has_video && (
                                      <Video className="w-3 h-3 text-blue-500" />
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </TooltipTrigger>
                        {content && (
                          <TooltipContent side="bottom" className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-medium">{content.title}</p>
                              <div className="flex items-center gap-2 text-xs">
                                <Badge variant="outline" className="text-xs">
                                  {content.status}
                                </Badge>
                                {content.has_voice && (
                                  <span className="flex items-center gap-1 text-green-600">
                                    <Mic className="w-3 h-3" /> Voice
                                  </span>
                                )}
                                {content.has_video && (
                                  <span className="flex items-center gap-1 text-blue-600">
                                    <Video className="w-3 h-3" /> Video
                                  </span>
                                )}
                              </div>
                              <Link to={`/scripts/${content.day_number}`}>
                                <Button size="sm" variant="link" className="h-auto p-0 text-accent">
                                  View Script →
                                </Button>
                              </Link>
                            </div>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </>
          ) : (
            /* List View */
            <div className="space-y-2" data-testid="calendar-list">
              {Object.entries(calendarData)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, content]) => (
                  <Link 
                    key={date} 
                    to={`/scripts/${content.day_number}`}
                    className="block"
                  >
                    <div className="flex items-center gap-4 p-4 rounded-lg hover:bg-muted/50 transition-colors border border-border/40">
                      <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                        <span className="font-display font-bold text-accent">
                          {content.day_number}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{content.title}</p>
                        <p className="text-sm text-muted-foreground">{date}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(content.status)}
                        <Badge variant="outline" className="hidden sm:inline-flex">
                          {content.status}
                        </Badge>
                        {content.has_voice && <Mic className="w-4 h-4 text-green-500" />}
                        {content.has_video && <Video className="w-4 h-4 text-blue-500" />}
                      </div>
                    </div>
                  </Link>
                ))}
              {Object.keys(calendarData).length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No scheduled content for this month</p>
                  <Link to="/scripts">
                    <Button variant="link" className="text-accent mt-2">
                      Generate scripts to get started
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="card-patriarch">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-6 justify-center">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Draft</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-accent" />
              <span className="text-sm text-muted-foreground">Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">Generating</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-sm text-muted-foreground">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-green-500" />
              <span className="text-sm text-muted-foreground">Has Voice</span>
            </div>
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-muted-foreground">Has Video</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-patriarch">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-display font-bold">
              {Object.values(calendarData).filter(v => v.status === 'completed').length}
            </p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
        <Card className="card-patriarch">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-display font-bold">
              {Object.values(calendarData).filter(v => v.has_voice).length}
            </p>
            <p className="text-sm text-muted-foreground">With Voice</p>
          </CardContent>
        </Card>
        <Card className="card-patriarch">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-display font-bold">
              {Object.values(calendarData).filter(v => v.has_video).length}
            </p>
            <p className="text-sm text-muted-foreground">With Video</p>
          </CardContent>
        </Card>
        <Card className="card-patriarch">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-display font-bold">
              {Object.keys(calendarData).length}
            </p>
            <p className="text-sm text-muted-foreground">Scheduled</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContentCalendar;
